import React, { useState } from 'react';
import { Search, Hash, Users, MapPin, UserCheck, Loader2, ExternalLink, Lock, CheckCircle } from 'lucide-react';

const API = 'https://api.glowytics.com/api/instagram-radar';

function formatCount(n) {
  if (!n) return '0';
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
}

const imgProxy = (url) => url ? `${API}/image-proxy?url=${encodeURIComponent(url)}` : null;

export default function InstagramSearchLab() {
  // Hashtag search
  const [hashtagQuery, setHashtagQuery] = useState('');
  const [hashtagResults, setHashtagResults] = useState(null);
  const [hashtagLoading, setHashtagLoading] = useState(false);

  // User search
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  // Similar accounts
  const [similarQuery, setSimilarQuery] = useState('');
  const [similarResults, setSimilarResults] = useState(null);
  const [similarLoading, setSimilarLoading] = useState(false);

  // Coordinates search
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [coordResults, setCoordResults] = useState(null);
  const [coordLoading, setCoordLoading] = useState(false);

  const [error, setError] = useState(null);

  const fetchApi = async (endpoint, params) => {
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(`${API}/${endpoint}?${qs}`, { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `API error ${res.status}`);
    }
    return res.json();
  };

  const searchHashtags = async (e) => {
    e.preventDefault();
    if (!hashtagQuery.trim()) return;
    setHashtagLoading(true);
    setError(null);
    try {
      const data = await fetchApi('search-hashtags', { q: hashtagQuery.trim() });
      setHashtagResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setHashtagLoading(false);
    }
  };

  const searchUsers = async (e) => {
    e.preventDefault();
    if (!userQuery.trim()) return;
    setUserLoading(true);
    setError(null);
    try {
      const data = await fetchApi('search-users', { q: userQuery.trim() });
      setUserResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUserLoading(false);
    }
  };

  const searchSimilar = async (e) => {
    e.preventDefault();
    if (!similarQuery.trim()) return;
    setSimilarLoading(true);
    setError(null);
    try {
      const data = await fetchApi('search-similar', { username: similarQuery.trim().replace(/^@/, '') });
      setSimilarResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSimilarLoading(false);
    }
  };

  const searchCoordinates = async (e) => {
    e.preventDefault();
    if (!lat || !lng) return;
    setCoordLoading(true);
    setError(null);
    try {
      const data = await fetchApi('search-coordinates', { latitude: lat, longitude: lng });
      setCoordResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setCoordLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Instagram Search Lab</h1>
        <p className="text-sm text-gray-500 mt-1">Hashtag, kullanıcı, benzer hesap ve lokasyon arama</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hashtag Search */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b bg-gradient-to-r from-pink-50 to-purple-50">
            <div className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-pink-600" />
              <h2 className="font-semibold text-gray-800">Hashtag Search</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">Instagram hashtag ara, media count gör</p>
          </div>
          <div className="p-4">
            <form onSubmit={searchHashtags} className="flex gap-2 mb-4">
              <input
                type="text"
                value={hashtagQuery}
                onChange={e => setHashtagQuery(e.target.value)}
                placeholder="iphone, fashion, istanbul..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <button type="submit" disabled={hashtagLoading}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-medium hover:bg-pink-700 disabled:opacity-50 flex items-center gap-1">
                {hashtagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Ara
              </button>
            </form>
            {hashtagResults && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="text-xs text-gray-500 mb-2">{hashtagResults.count} sonuç</div>
                {hashtagResults.items.map((h, i) => (
                  <div key={h.id || i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-pink-500 font-medium">#</span>
                      <span className="text-sm font-medium text-gray-800 truncate">{h.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-600 bg-white px-2 py-1 rounded-full border">
                        {formatCount(h.media_count)} post
                      </span>
                      <a href={`https://www.instagram.com/explore/tags/${h.name}/`} target="_blank" rel="noreferrer"
                        className="text-gray-400 hover:text-pink-600" title="Instagram'da aç">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* User Search */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="font-semibold text-gray-800">User Search</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">Instagram kullanıcı ara</p>
          </div>
          <div className="p-4">
            <form onSubmit={searchUsers} className="flex gap-2 mb-4">
              <input
                type="text"
                value={userQuery}
                onChange={e => setUserQuery(e.target.value)}
                placeholder="nike, zara, shakira..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button type="submit" disabled={userLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                {userLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Ara
              </button>
            </form>
            {userResults && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="text-xs text-gray-500 mb-2">{userResults.count} sonuç</div>
                {userResults.items.map((u, i) => (
                  <div key={u.id || i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    {u.profile_pic_url ? (
                      <img src={imgProxy(u.profile_pic_url)} alt="" className="w-9 h-9 rounded-full object-cover bg-gray-200" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <a href={`https://www.instagram.com/${u.username}/`} target="_blank" rel="noreferrer"
                          className="text-sm font-medium text-gray-800 hover:text-blue-600 truncate">@{u.username}</a>
                        {u.is_verified && <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />}
                        {u.is_private && <Lock className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                      </div>
                      {u.full_name && <div className="text-xs text-gray-500 truncate">{u.full_name}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Similar Accounts */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-gray-800">Similar Accounts</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">Bir hesaba benzer hesapları bul</p>
          </div>
          <div className="p-4">
            <form onSubmit={searchSimilar} className="flex gap-2 mb-4">
              <input
                type="text"
                value={similarQuery}
                onChange={e => setSimilarQuery(e.target.value)}
                placeholder="@shakira, nike, zara..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button type="submit" disabled={similarLoading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                {similarLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Bul
              </button>
            </form>
            {similarResults && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="text-xs text-gray-500 mb-2">{similarResults.count} benzer hesap</div>
                {similarResults.items.map((u, i) => (
                  <div key={u.id || i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    {u.profile_pic_url ? (
                      <img src={imgProxy(u.profile_pic_url)} alt="" className="w-9 h-9 rounded-full object-cover bg-gray-200" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <a href={`https://www.instagram.com/${u.username}/`} target="_blank" rel="noreferrer"
                          className="text-sm font-medium text-gray-800 hover:text-emerald-600 truncate">@{u.username}</a>
                        {u.is_verified && <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />}
                        {u.is_private && <Lock className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                      </div>
                      {u.full_name && <div className="text-xs text-gray-500 truncate">{u.full_name}</div>}
                    </div>
                    <a href={`https://www.instagram.com/${u.username}/`} target="_blank" rel="noreferrer"
                      className="text-gray-400 hover:text-gray-600">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Coordinates Search */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              <h2 className="font-semibold text-gray-800">Location Search</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">Koordinata göre Instagram lokasyonlarını bul</p>
          </div>
          <div className="p-4">
            <form onSubmit={searchCoordinates} className="flex gap-2 mb-4">
              <input
                type="text"
                value={lat}
                onChange={e => setLat(e.target.value)}
                placeholder="Latitude (41.0082)"
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <input
                type="text"
                value={lng}
                onChange={e => setLng(e.target.value)}
                placeholder="Longitude (28.9784)"
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button type="submit" disabled={coordLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1">
                {coordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                Ara
              </button>
            </form>
            <div className="flex gap-2 mb-4">
              <button type="button" onClick={() => { setLat('41.0082'); setLng('28.9784'); }}
                className="text-xs px-2.5 py-1 bg-gray-100 rounded-full hover:bg-gray-200 text-gray-600">Istanbul</button>
              <button type="button" onClick={() => { setLat('40.7128'); setLng('-74.0060'); }}
                className="text-xs px-2.5 py-1 bg-gray-100 rounded-full hover:bg-gray-200 text-gray-600">New York</button>
              <button type="button" onClick={() => { setLat('39.9334'); setLng('32.8597'); }}
                className="text-xs px-2.5 py-1 bg-gray-100 rounded-full hover:bg-gray-200 text-gray-600">Ankara</button>
            </div>
            {coordResults && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="text-xs text-gray-500 mb-2">{coordResults.count} lokasyon</div>
                {coordResults.items.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800">{p.name}</div>
                        <div className="text-xs text-gray-500">
                          {p.address && <span>{p.address}</span>}
                          {p.category && <span> · {p.category}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
